import React from 'react'
import { LuCompass } from 'react-icons/lu'
import { useNavigate } from 'react-router'

import SearchResultIcon from '@/components/misc/GlobalSearch/components/SearchResultIcon'
import SearchResultItem from '@/components/misc/GlobalSearch/components/SearchResultItem'
import { SEARCH_RESULT_ICON_COLORS } from '@/components/misc/GlobalSearch/constants'
import { useGlobalSearch } from '@/components/misc/GlobalSearch/hooks/useGlobalSearch'
import { CommandGroup } from '@/components/ui/command'
import { DATE_FORMATS } from '@/lib/constants'
import { formatDate } from '@/lib/helpers/dateHelpers'
import { ROUTES } from '@/lib/routes'
import { CompassSnapshotSearchResult } from '@/types/responses/companies'

interface Props {
  compassSnapshots: CompassSnapshotSearchResult[]
  /** Starting pop-in index, so snapshots animate in after the clients. */
  indexOffset?: number
}

const CompassSnapshotResults: React.FC<Props> = ({
  compassSnapshots,
  indexOffset = 0,
}) => {
  const globalSearch = useGlobalSearch()
  const navigate = useNavigate()

  if (compassSnapshots.length === 0) {
    return null
  }

  return (
    <CommandGroup heading="Compass snapshots">
      {compassSnapshots.map(
        ({ client, compassReport, sharedByUserName }, index) => (
          <SearchResultItem
            key={compassReport['@id']}
            index={indexOffset + index}
            value={`compass-snapshot-${compassReport['@id']}`}
            className="flex items-center gap-3"
            onSelect={() => {
              globalSearch.hide()
              // Open the snapshot in the Compass Snapshots page's modal
              navigate(
                ROUTES.companyCompassSnapshots({
                  searchParams: {
                    compassSnapshotModalToken: compassReport.token,
                  },
                }),
              )
            }}
          >
            <SearchResultIcon
              icon={LuCompass}
              color={SEARCH_RESULT_ICON_COLORS.purple}
            />

            {/* text */}
            <div className="flex flex-1 flex-col gap-0 items-stretch">
              <span className="font-medium">{client.fullNameAndPartyName}</span>

              {/* A portal user's snapshot can be listed under a client with a
                different name, so say whose it is */}
              {sharedByUserName && (
                <div className="text-muted-foreground text-xs">
                  Shared by {sharedByUserName}
                </div>
              )}

              {compassReport.completedAt && (
                <div className="text-muted-foreground text-xs">
                  Completed{' '}
                  {formatDate(
                    compassReport.completedAt,
                    DATE_FORMATS.DAY_MONTH_YEAR,
                  )}
                </div>
              )}
            </div>
          </SearchResultItem>
        ),
      )}
    </CommandGroup>
  )
}

export default CompassSnapshotResults
